/**
 * Task Assignment Page - Assign tasks to team members
 */

import React from 'react';
import { Navigate, useNavigate } from 'react-router';
import { updateTask } from '../store';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Users, Save } from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { filterVisibleTasksForUser, getProjectTeamUsers } from '../utils/projectAccess';
import ReportSectionCard from '../components/ReportSectionCard.tsx';
import { WIDGET_MUTED_TEXT_CLASS, WIDGET_SUBTLE_PANEL_CLASS } from '../constants/widgetTheme';

export default function TaskAssignment() {
  const navigate = useNavigate();
  const { state, loading } = useApp();
  const [assignments, setAssignments] = React.useState<Record<string, string[]>>({});

  if (loading || !state) {
    return <div className={`p-8 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>Loading task assignments...</div>;
  }

  if (!state.currentUser) {
    return <div className={`p-8 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>User context not loaded</div>;
  }

  if (state.currentUser.role === 'external_partner') {
    return <Navigate to="/app/dashboard" replace />;
  }

  // Get current organization's data
  const allUsers = state.users;
  const projectMap = React.useMemo(
    () => new Map(state.projects.map((project) => [project.id, project])),
    [state.projects]
  );
  const orgTasks = React.useMemo(
    () => filterVisibleTasksForUser(
      state.currentUser,
      state.tasks.filter((task) => task.organizationId === state.currentOrganization?.id),
      (task) => projectMap.get(task.projectId) || null
    ),
    [projectMap, state.currentOrganization?.id, state.currentUser, state.tasks]
  );

  const taskAssignableUsers = React.useMemo(() => {
    const assignableByTask: Record<string, typeof allUsers> = {};

    orgTasks.forEach((task) => {
      const project = projectMap.get(task.projectId);
      assignableByTask[task.id] = project ? getProjectTeamUsers(project, allUsers, state.teams || []) : [];
    });

    return assignableByTask;
  }, [orgTasks, allUsers, projectMap, state.teams]);

  const visibleTeamMembers = React.useMemo(() => {
    const memberMap = new Map<string, (typeof allUsers)[number]>();

    Object.values(taskAssignableUsers).forEach((users) => {
      users.forEach((user) => {
        memberMap.set(user.id, user);
      });
    });

    return Array.from(memberMap.values());
  }, [taskAssignableUsers]);

  // Initialize assignments from existing data
  React.useEffect(() => {
    const initialAssignments: Record<string, string[]> = {};
    orgTasks.forEach(task => {
      const assignableIds = new Set((taskAssignableUsers[task.id] || []).map((user) => user.id));
      initialAssignments[task.id] = (task.assigneeIds || []).filter((assigneeId) =>
        assignableIds.has(assigneeId)
      );
    });
    setAssignments((previousAssignments) => {
      const previousKeys = Object.keys(previousAssignments);
      const nextKeys = Object.keys(initialAssignments);

      if (
        previousKeys.length === nextKeys.length &&
        nextKeys.every((taskId) => {
          const previousTaskAssignments = [...(previousAssignments[taskId] || [])].sort();
          const nextTaskAssignments = [...(initialAssignments[taskId] || [])].sort();

          return JSON.stringify(previousTaskAssignments) === JSON.stringify(nextTaskAssignments);
        })
      ) {
        return previousAssignments;
      }

      return initialAssignments;
    });
  }, [orgTasks, taskAssignableUsers]);

  const handleToggleAssignment = (taskId: string, userId: string) => {
    setAssignments(prev => {
      const assignableIds = new Set((taskAssignableUsers[taskId] || []).map((user) => user.id));
      if (!assignableIds.has(userId)) {
        return prev;
      }

      const current = prev[taskId] || [];
      const isAssigned = current.includes(userId);
      
      return {
        ...prev,
        [taskId]: isAssigned 
          ? current.filter(id => id !== userId)
          : [...current, userId]
      };
    });
  };

  const handleSaveAssignments = async () => {
    let updated = 0;
    
    for (const [taskId, assigneeIds] of Object.entries(assignments)) {
      const task = orgTasks.find(t => t.id === taskId);
      if (task) {
        const assignableIds = new Set((taskAssignableUsers[taskId] || []).map((user) => user.id));
        const sanitizedAssigneeIds = assigneeIds.filter((assigneeId) => assignableIds.has(assigneeId));

        const hasChanged =
          JSON.stringify([...(task.assigneeIds || [])].sort()) !==
          JSON.stringify([...sanitizedAssigneeIds].sort());

        if (hasChanged) {
          await updateTask(taskId, { assigneeIds: sanitizedAssigneeIds });
          updated++;
        }
      }
    }
    
    toast.success(`Updated ${updated} task assignments`);
    navigate('/app/dashboard');
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
        <div className="mb-4 sm:mb-5">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Task Assignment</h1>
              <p className={`mt-1 text-xs sm:text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                Assign tasks to team members in {state.currentOrganization?.name}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate('/app/dashboard')}>
                Cancel
              </Button>
              <Button onClick={handleSaveAssignments}>
                <Save className="mr-2 h-4 w-4" />
                Save Assignments
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-6">
          <ReportSectionCard
            title="Team Members"
            subtitle={`${visibleTeamMembers.length} assignable teammates`}
            bodyClassName="p-4"
          >
            <div className="space-y-3">
              {visibleTeamMembers.map(user => (
                <div key={user.id} className={`${WIDGET_SUBTLE_PANEL_CLASS} flex items-center gap-3 p-3`}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-sm font-medium text-foreground">
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{user.name}</div>
                    <div className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>{user.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </ReportSectionCard>

          <ReportSectionCard
            title="Tasks"
            subtitle={`${orgTasks.length} visible tasks`}
            bodyClassName="p-4"
          >
            <div className="space-y-3">
              {orgTasks.slice(0, 50).map(task => (
                <div key={task.id} className={`${WIDGET_SUBTLE_PANEL_CLASS} p-4 transition-colors hover:bg-accent/20`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                          {task.id}
                        </Badge>
                        <Badge variant="secondary">{task.priority}</Badge>
                      </div>
                      <h3 className="mb-1 font-semibold text-foreground">{task.title}</h3>
                      {task.description && (
                        <p className={`mb-3 line-clamp-2 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                          {task.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-3">
                        {(taskAssignableUsers[task.id] || []).map(user => (
                          <label
                            key={user.id}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-accent/30"
                          >
                            <Checkbox
                              checked={(assignments[task.id] || []).includes(user.id)}
                              onCheckedChange={() => handleToggleAssignment(task.id, user.id)}
                            />
                            <span className="text-sm text-foreground">{user.name.split(' ')[0]}</span>
                          </label>
                        ))}

                        {(taskAssignableUsers[task.id] || []).length === 0 && (
                          <p className={`text-sm italic ${WIDGET_MUTED_TEXT_CLASS}`}>
                            No project team members available for this task.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ReportSectionCard>
        </div>
    </div>
  );
}
